import { trpcClientUtils } from 'api/ApiProvider'
import { useOrder } from '@order/hooks/useOrderState'
import useSession from 'hooks/useSession'
import { useState } from 'react'
import { TextInput, View } from 'react-native'
import Typography from '@ui/typography'
import { getPrescriptionByIdFromCache } from '@order/hooks/usePrescription'
import Button from '@ui/buttons/Button'
import { useRefractionDebugStore } from '../useRefractionDebugStore'

type Fields = {
  r_sph: string
  l_sph: string
  r_cyl: string
  l_cyl: string
  r_axis: string
  l_axis: string
  r_add: string
  l_add: string
}

const rows: { label: string; r: keyof Fields; l: keyof Fields }[] = [
  { label: 'SPH', r: 'r_sph', l: 'l_sph' },
  { label: 'CYL', r: 'r_cyl', l: 'l_cyl' },
  { label: 'AXIS', r: 'r_axis', l: 'l_axis' },
  { label: 'ADD', r: 'r_add', l: 'l_add' },
]

const toStr = (v: number | null | undefined) => (v != null ? String(v) : '0')

const emptyFields: Fields = {
  r_sph: '0',
  l_sph: '0',
  r_cyl: '0',
  l_cyl: '0',
  r_axis: '0',
  l_axis: '0',
  r_add: '0',
  l_add: '0',
}

const toFields = (
  p: NonNullable<ReturnType<typeof getPrescriptionByIdFromCache>>
): Fields => ({
  r_sph: toStr(p.r_sph),
  l_sph: toStr(p.l_sph),
  r_cyl: toStr(p.r_cyl),
  l_cyl: toStr(p.l_cyl),
  r_axis: toStr(p.r_axis),
  l_axis: toStr(p.l_axis),
  r_add: toStr(p.r_add),
  l_add: toStr(p.l_add),
})

export default function RefractionOverrideView() {
  const prescriptionId = useOrder().prescriptionId
  const customerId = useSession((s) => s.customer?.data.id)
  const [prevPrescriptionId, setPrevPrescriptionId] = useState(prescriptionId)
  const [fields, setFields] = useState<Fields>(() => {
    const p = getPrescriptionByIdFromCache(prescriptionId, customerId)
    return p ? toFields(p) : emptyFields
  })
  const overrideActive = useRefractionDebugStore((s) => s.overrideActive)
  const setOverrideActive = useRefractionDebugStore((s) => s.setOverrideActive)

  if (prevPrescriptionId !== prescriptionId) {
    setPrevPrescriptionId(prescriptionId)
    const p = getPrescriptionByIdFromCache(prescriptionId, customerId)
    setFields(p ? toFields(p) : emptyFields)
    setOverrideActive(false)
  }

  if (!prescriptionId || !customerId) {
    return (
      <View className="p-2">
        <Typography variant="XS" className="text-slate-400">
          No prescription selected
        </Typography>
      </View>
    )
  }

  const apply = () => {
    const current = getPrescriptionByIdFromCache(prescriptionId, customerId)
    if (!current) return
    trpcClientUtils.customer.prescriptions.byId.setData(
      { prescriptionId, customerId },
      {
        ...current,
        ...Object.fromEntries(
          Object.entries(fields).map(([k, v]) => [k, Number(v)])
        ),
      }
    )
    setOverrideActive(true)
  }

  const reset = () => {
    trpcClientUtils.customer.prescriptions.byId.invalidate({
      prescriptionId,
      customerId,
    })
    setOverrideActive(false)
  }

  const set = (key: keyof Fields) => (val: string) =>
    setFields((prev) => ({ ...prev, [key]: val }))

  return (
    <View className="gap-3">
      {overrideActive && (
        <View className="rounded-md bg-amber-200 p-2">
          <Typography variant="XS" className="text-amber-700">
            Override active
          </Typography>
        </View>
      )}

      <View className="gap-2">
        <View className="flex-row items-center">
          <View className="w-12" />
          <Typography
            variant="XSB"
            className="flex-1 text-center text-slate-500"
          >
            R
          </Typography>
          <Typography
            variant="XSB"
            className="flex-1 text-center text-slate-500"
          >
            L
          </Typography>
        </View>
        {rows.map(({ label, r, l }) => (
          <View key={label} className="flex-row items-center gap-2">
            <Typography variant="XSB" className="w-12 text-slate-600">
              {label}
            </Typography>
            <TextInput
              className="rounded font-mono flex-1 rounded-lg border border-slate-300 bg-white p-2 text-center text-sm"
              value={fields[r]}
              onChangeText={set(r)}
              keyboardType="numeric"
              selectTextOnFocus
            />
            <TextInput
              className="rounded font-mono flex-1 rounded-lg border border-slate-300 bg-white p-2 text-center text-sm"
              value={fields[l]}
              onChangeText={set(l)}
              keyboardType="numeric"
              selectTextOnFocus
            />
          </View>
        ))}
      </View>

      <View className="flex gap-2 last:flex-row">
        <Button variant="filled" label="Apply" onPress={apply} />
        <Button variant="outlined" label="Reset" onPress={reset} />
      </View>
    </View>
  )
}
